import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import { Download, QrCode, Star, Trash2, Printer, UserCheck, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { key: 'uniform_rating', label: 'Uniform', avgKey: 'uniform' },
  { key: 'engagement_rating', label: 'Engagement', avgKey: 'engagement' },
  { key: 'information_rating', label: 'Information', avgKey: 'information' },
  { key: 'communication_rating', label: 'Communication', avgKey: 'communication' },
  { key: 'professionalism_rating', label: 'Professionalism', avgKey: 'professionalism' },
  { key: 'friendliness_rating', label: 'Friendliness', avgKey: 'friendliness' },
  { key: 'helpfulness_rating', label: 'Helpfulness', avgKey: 'helpfulness' },
  { key: 'appearance_rating', label: 'Appearance', avgKey: 'appearance' },
  { key: 'response_time_rating', label: 'Response time', avgKey: 'response_time' },
];

function Stars({ value }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={`w-4 h-4 ${n <= value ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground/40'}`}
          strokeWidth={1.5}
        />
      ))}
    </div>
  );
}

export function PublicFeedbackContent() {
  const { user: authUser } = useAuth();
  const isSupervisor = authUser?.role === 'supervisor';
  const [qrUrl, setQrUrl] = useState('');
  const [data, setData] = useState({ count: 0, averages: {}, items: [] });
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [employees, setEmployees] = useState([]);
  const [attributingId, setAttributingId] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [qrRes, listRes, empsRes] = await Promise.all([
        axios.get(`${API}/admin/public-feedback/qr-url`),
        axios.get(`${API}/admin/public-feedback`),
        axios.get(`${API}/admin/clock/all-employees`),
      ]);
      setQrUrl(qrRes.data.url);
      setData(listRes.data);
      setEmployees(empsRes.data.employees || []);
    } catch (err) {
      toast.error('Failed to load public feedback');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleAttribute = async (feedbackId, employeeId) => {
    if (!employeeId) return;
    setAttributingId(feedbackId);
    try {
      const res = await axios.post(`${API}/admin/public-feedback/${feedbackId}/attribute`, { employee_id: employeeId });
      toast.success(`Attributed to ${res.data.employee_name}`);
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not attribute');
    } finally {
      setAttributingId(null);
    }
  };

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const downloadPDF = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/admin/public-feedback/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'security-staff-feedback-qr.pdf';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('PDF downloaded — ready to print');
    } catch (err) {
      toast.error('Failed to generate PDF');
    } finally {
      setDownloading(false);
    }
  };

  const printPDF = async () => {
    try {
      const res = await axios.get(`${API}/admin/public-feedback/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const w = window.open(url);
      if (w) w.onload = () => w.print();
    } catch (err) {
      toast.error('Failed to open PDF for printing');
    }
  };

  const confirmDelete = async () => {
    if (!deleteDialog.id) return;
    try {
      await axios.delete(`${API}/admin/public-feedback/${deleteDialog.id}`);
      toast.success('Feedback deleted');
      setDeleteDialog({ open: false, id: null });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <>
      <div className="space-y-6">
        {/* QR PDF card */}
        <Card className="bg-card border-border rounded-sm">
          <CardHeader className="border-b border-border">
            <CardTitle className="flex items-center gap-2 font-barlow tracking-wider uppercase">
              <QrCode className="w-5 h-5 text-primary" strokeWidth={1.5} />
              Printable QR Code
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 space-y-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="flex-1 min-w-0">
                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Public feedback link</p>
                <p className="text-sm font-mono break-all">{qrUrl || '—'}</p>
              </div>
              <div className="flex gap-2 w-full sm:w-auto">
                <Button
                  onClick={downloadPDF}
                  disabled={downloading}
                  className="bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm flex-1 sm:flex-none"
                  data-testid="download-feedback-pdf-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {downloading ? 'Generating...' : 'Download PDF'}
                </Button>
                <Button
                  onClick={printPDF}
                  variant="outline"
                  className="border-border rounded-sm flex-1 sm:flex-none"
                  data-testid="print-feedback-pdf-btn"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Print
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Averages summary */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {CATEGORIES.map((c) => (
            <Card key={c.key} className="bg-card border-border rounded-sm">
              <CardContent className="p-4">
                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">{c.label}</p>
                <p className="text-2xl font-semibold">{data.averages[c.avgKey] || 0}<span className="text-sm text-muted-foreground">/5</span></p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Feedback Cards manager moved to its own page — see
            /admin/feedback-cards in the sidebar. */}

        {/* Responses list */}
        <Card className="bg-card border-border rounded-sm">
          <CardHeader className="border-b border-border flex-row items-center justify-between">
            <CardTitle className="font-barlow tracking-wider uppercase">
              Responses {data.count > 0 && <span className="text-muted-foreground">({data.count})</span>}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <p className="p-6 text-sm text-muted-foreground">Loading...</p>
            ) : data.items.length === 0 ? (
              <p className="p-6 text-sm text-muted-foreground text-center">No feedback received yet</p>
            ) : (
              <div className="divide-y divide-border">
                {data.items.map((it) => {
                  const status = it.attribution_status || 'unassigned';
                  const candidates = (it.card_candidate_ids || []).map((id, i) => ({
                    id,
                    name: (it.card_candidate_names || [])[i] || 'Unknown',
                  }));
                  return (
                  <div key={it.id} className="p-4 sm:p-6 space-y-3" data-testid={`feedback-row-${it.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <Badge variant="outline" className="text-xs border-border">
                            {it.reviewer_type === 'staff' ? 'Staff' : 'Public'}
                          </Badge>
                          {status === 'attributed' && (
                            <Badge className="text-[10px] bg-green-600 hover:bg-green-600" data-testid={`fb-attributed-${it.id}`}>
                              <UserCheck className="w-3 h-3 mr-1" />
                              {it.attributed_employee_name}
                            </Badge>
                          )}
                          {status === 'needs_review' && (
                            <Badge className="text-[10px] bg-orange-600 hover:bg-orange-600" data-testid={`fb-needs-review-${it.id}`}>
                              <AlertCircle className="w-3 h-3 mr-1" />
                              Needs review
                            </Badge>
                          )}
                          {status === 'unassigned' && (
                            <Badge variant="outline" className="text-[10px] border-zinc-600 text-zinc-400">
                              Unassigned
                            </Badge>
                          )}
                          <span className="text-xs text-muted-foreground">
                            {new Date(it.created_at).toLocaleString('en-GB', { timeZone: 'Europe/London' })}
                          </span>
                        </div>
                        <p className="text-sm font-medium">{it.location_or_event}</p>
                        <p className="text-xs text-muted-foreground">Feedback date: {it.feedback_date}</p>
                      </div>
                      {!isSupervisor && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeleteDialog({ open: true, id: it.id })}
                          data-testid={`delete-feedback-${it.id}`}
                          className="text-red-400 hover:bg-red-500/10 rounded-sm"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>

                    {/* Attribution picker — shown for needs_review (with the
                        actual candidates) or unassigned (with full staff). */}
                    {status !== 'attributed' && (
                      <div className="flex flex-wrap items-center gap-2 p-2 bg-background rounded-sm border border-border">
                        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                          {status === 'needs_review' ? 'Pick the right person:' : 'Attribute to:'}
                        </span>
                        <Select
                          onValueChange={(v) => handleAttribute(it.id, v)}
                          disabled={attributingId === it.id}
                        >
                          <SelectTrigger className="h-7 text-xs w-[220px] bg-card border-border" data-testid={`fb-attribute-picker-${it.id}`}>
                            <SelectValue placeholder={attributingId === it.id ? 'Saving…' : 'Choose employee…'} />
                          </SelectTrigger>
                          <SelectContent className="bg-card border-border max-h-60">
                            {(candidates.length > 0 ? candidates : employees).map((e) => (
                              <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
                      {CATEGORIES.filter((c) => it[c.key]).map((c) => (
                        <div key={c.key} className="flex items-center justify-between sm:flex-col sm:items-start gap-1 p-2 bg-background rounded-sm border border-border">
                          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{c.label}</span>
                          <Stars value={it[c.key]} />
                        </div>
                      ))}
                    </div>

                    {it.comment && (
                      <div className="p-3 bg-background rounded-sm border border-border">
                        <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Comment</p>
                        <p className="text-sm whitespace-pre-wrap">{it.comment}</p>
                      </div>
                    )}

                    {(it.name || it.email) && (
                      <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                        {it.name && <span>From: <span className="text-foreground font-medium">{it.name}</span></span>}
                        {it.email && <span>Email: <span className="text-foreground font-medium">{it.email}</span></span>}
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, id: null })}>
        <AlertDialogContent className="bg-card border-border rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this feedback?</AlertDialogTitle>
            <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-border rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-500 text-white rounded-sm">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
